import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, DocumentTextOutline, Flex, Link, rem, Text } from '@bonusly/design-system';

import { GenericAttachment } from '../../types/generic-attachment';
import { buildPdfPreviewUrl } from './is-pdf-attachment-url';

type PdfAttachmentPreviewProps = {
	attachment: GenericAttachment;
};

export const PdfAttachmentPreview: FC<PdfAttachmentPreviewProps> = ({ attachment }) => {
	const { t } = useTranslation('recognition', { keyPrefix: 'bonus_card' });

	if (!attachment.url) {
		return null;
	}

	const title = attachment.title || t('pdf_preview');
	const previewUrl = buildPdfPreviewUrl(attachment.url);

	return (
		<Box bg="bg.surface" borderRadius="lg" border="1px solid" borderColor="border.default" overflow="hidden" data-testid="pdf-attachment-preview">
			<Box position="relative" maxH={rem(200)} overflow="hidden" bg="bg.subtle" aria-hidden>
				<Box
					as="iframe"
					src={previewUrl}
					title={title}
					width="100%"
					height={rem(320)}
					border="none"
					pointerEvents="none"
					tabIndex={-1}
					data-testid="pdf-attachment-frame"
				/>
			</Box>
			<Box p={3} borderTopWidth="1px" borderColor="border.default">
				<Flex alignItems="center" gap={2} mb={1}>
					<DocumentTextOutline color="secondary.fg" flexShrink={0} size="sm" />
					{attachment.title && (
						<Text fontWeight="semibold" fontSize="sm" color="text.default" noOfLines={2} data-testid="pdf-attachment-title">
							{attachment.title}
						</Text>
					)}
				</Flex>
				<Link
					href={attachment.url}
					target="_blank"
					rel="noopener noreferrer"
					color="text.interactive"
					fontSize="sm"
					fontWeight="medium"
					data-testid="pdf-attachment-open-link"
				>
					{t('open_pdf')}
				</Link>
			</Box>
		</Box>
	);
};
